/**
 * File-summary panel for an analyzed session. From desktop up it sits beside
 * the chat column; below that it scrolls inside the thread (CHAT-1).
 */
import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import { useTranslation } from '../../../hooks/useTranslation';
import { webChatStyles } from '../chatStyles';

export interface ChatContextPanelProps {
  title: string;
  messageCount: number;
  isPhone: boolean;
  isCompact: boolean;
  canOpenTranscript: boolean;
  canExportObservationPdf: boolean;
  canShareTranscript: boolean;
  onOpenTranscript: () => void;
  onExportPdf: (reportType: 'standard' | 'observation') => void;
  onShareTranscript: () => void;
}

export function ChatContextPanel({
  title,
  messageCount,
  isPhone,
  isCompact,
  canOpenTranscript,
  canExportObservationPdf,
  canShareTranscript,
  onOpenTranscript,
  onExportPdf,
  onShareTranscript,
}: ChatContextPanelProps) {
  const { t, language } = useTranslation();

  return (
    <View
      style={[
        webChatStyles.contextPanel,
        isCompact && webChatStyles.contextPanelCompact,
        isPhone && webChatStyles.contextPanelPhone,
      ]}
      testID="chat_context_panel"
    >
      <Text style={webChatStyles.contextTitle}>{t('chat.webFileSummary')}</Text>
      <View style={webChatStyles.contextFileCard}>
        <MaterialIcons name="description" size={24} color={reviewDesk.accent} />
        <View style={{ flex: 1, minWidth: isPhone ? 0 : undefined }}>
          <Text style={webChatStyles.contextFileTitle} numberOfLines={1}>{title}</Text>
          <Text style={webChatStyles.contextMeta}>{t('chat.webSecurelySaved')}</Text>
        </View>
      </View>
      <View style={webChatStyles.contextSection}>
        <Text style={webChatStyles.contextSectionTitle}>{t('chat.webAnalysisSummary')}</Text>
        {[
          [t('chat.webType'), t('chat.webIcfAssessment')],
          [t('chat.webLanguage'), language === 'en' ? 'English' : 'Türkçe'],
          [t('chat.webLevel'), t('analysis.modal.levelAuto')],
          [t('chat.webMessages'), String(messageCount)],
        ].map(([label, value]) => (
          <View key={label} style={[webChatStyles.contextRow, isPhone && webChatStyles.contextRowPhone]}>
            <Text style={webChatStyles.contextLabel}>{label}</Text>
            <Text style={[webChatStyles.contextValue, isPhone && webChatStyles.contextValuePhone]}>
              {value}
            </Text>
          </View>
        ))}
      </View>
      {canOpenTranscript && (
        <TouchableOpacity style={webChatStyles.downloadButton} onPress={onOpenTranscript} accessibilityRole="button">
          <MaterialIcons name="article" size={17} color={reviewDesk.accent} />
          <Text style={webChatStyles.downloadButtonText}>{t('library.openTranscript')}</Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity testID="download-standard-pdf" style={webChatStyles.downloadButton} onPress={() => onExportPdf('standard')} accessibilityRole="button">
        <MaterialIcons name="download" size={17} color={reviewDesk.accent} />
        <Text style={webChatStyles.downloadButtonText}>{t('chat.downloadStandardPdf')}</Text>
      </TouchableOpacity>
      {canExportObservationPdf && (
        <TouchableOpacity testID="download-observation-pdf" style={webChatStyles.downloadButton} onPress={() => onExportPdf('observation')} accessibilityRole="button">
          <MaterialIcons name="assignment" size={17} color={reviewDesk.accent} />
          <Text style={webChatStyles.downloadButtonText}>{t('chat.downloadObservationPdf')}</Text>
        </TouchableOpacity>
      )}
      {canShareTranscript && (
        <TouchableOpacity style={webChatStyles.downloadButton} onPress={onShareTranscript} accessibilityRole="button">
          <MaterialIcons name="ios-share" size={17} color={reviewDesk.accent} />
          <Text style={webChatStyles.downloadButtonText}>{t('library.shareWithMentor')}</Text>
        </TouchableOpacity>
      )}
      <View style={webChatStyles.contextNote}>
        <MaterialIcons name="info-outline" size={18} color={reviewDesk.amber} />
        <Text style={webChatStyles.contextNoteText}>
          {t('chat.webProgressNote')}
        </Text>
      </View>
    </View>
  );
}
